import { HttpClient } from '@angular/common/http';
import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { interval, Observable, takeUntil, Subject, catchError, of } from 'rxjs';
import { prodAPI } from '../../../env';
import { Timer } from '../../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class TimerService {
  private stopPolling$ = new Subject<void>();
  private countdownInterval$ = new Subject<void>();
  private previousRemainingSeconds: number = 0;

  public readonly timer: WritableSignal<Timer | null> = signal<Timer | null>(null);
  public readonly timeDisplay: WritableSignal<string> = signal<string>('00:00:00');
  public readonly isActive: WritableSignal<boolean> = signal<boolean>(false);
  public readonly animationTrigger: WritableSignal<number> = signal<number>(0);

  constructor(private http: HttpClient) {
    // Убираем инициализацию previousRemainingSeconds здесь
  }

  startPolling(intervalTime: number = 1000): void {
    // Initial request
    this.fetchTimer();

    // Poll API every 10 seconds
    interval(10000)
      .pipe(
        takeUntil(this.stopPolling$)
      )
      .subscribe(() => {
        this.fetchTimer();
      });
  }

  stopPolling(): void {
    this.stopPolling$.next();
    this.countdownInterval$.next();
  }

  private fetchTimer(): void {
    this.http.get<Timer>(`${prodAPI}/api/timer`)
      .pipe(
        catchError((err) => {
          console.error('Error fetching timer:', err);
          return of(null);
        })
      )
      .subscribe({
        next: (timer) => {
          if (timer) {
            // Убираем проверку previousRemainingSeconds > 0
            if (timer.remaining_seconds > this.previousRemainingSeconds) {
              console.log(`Timer increased from ${this.previousRemainingSeconds}s to ${timer.remaining_seconds}s - triggering animation`);
              this.animationTrigger.set(Date.now());
              
              setTimeout(() => {
                this.animationTrigger.set(0);
              }, 4120);
            }
            this.previousRemainingSeconds = timer.remaining_seconds;
            
            this.timer.set(timer);
            this.isActive.set(timer.is_active);
            this.startCountdown(timer.remaining_seconds);
          } else {
            // Mock data when API returns empty response
            const mockTimer: Timer = {
              is_active: true,
              start_time: new Date().toISOString(),
              end_time: new Date(Date.now() + 3600000).toISOString(),
              remaining_seconds: 3600
            };
            
            // Убираем проверку previousRemainingSeconds > 0
            if (mockTimer.remaining_seconds > this.previousRemainingSeconds) {
              console.log(`Mock timer increased from ${this.previousRemainingSeconds}s to ${mockTimer.remaining_seconds}s - triggering animation`);
              this.animationTrigger.set(Date.now());
              
              setTimeout(() => {
                this.animationTrigger.set(0);
              }, 4120);
            }
            this.previousRemainingSeconds = mockTimer.remaining_seconds;
            
            this.timer.set(mockTimer);
            this.isActive.set(mockTimer.is_active);
            this.startCountdown(mockTimer.remaining_seconds);
          }
        }
      });
  }

  private startCountdown(seconds: number): void {
    // Stop previous countdown
    this.countdownInterval$.next();
    
    if (seconds <= 0) {
      this.timeDisplay.set('00:00:00');
      return;
    }

    // Start new countdown
    interval(1000)
      .pipe(
        takeUntil(this.countdownInterval$)
      )
      .subscribe(() => {
        seconds--;
        if (seconds >= 0) {
          this.updateTimeDisplay(seconds);
        } else {
          this.timeDisplay.set('00:00:00');
          this.countdownInterval$.next();
        }
      });
  }

  private updateTimeDisplay(seconds: number): void {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    this.timeDisplay.set(timeString);
  }

  getTimer(): Observable<Timer> {
    return this.http.get<Timer>(`${prodAPI}/api/timer`);
  }

  getTimerSignal(): Signal<Timer | null> {
    return this.timer.asReadonly();
  }

  getTimeDisplaySignal(): Signal<string> {
    return this.timeDisplay.asReadonly();
  }

  getIsActiveSignal(): Signal<boolean> {
    return this.isActive.asReadonly();
  }

  getAnimationTriggerSignal(): Signal<number> {
    return this.animationTrigger.asReadonly();
  }

  // Убираем тестовый метод
  // triggerTestAnimation(): void {
  //   console.log('Test animation triggered manually');
  //   this.animationTrigger.set(Date.now());
  //   
  //   // Hide animation after video duration (4.12 seconds from webm metadata)
  //   setTimeout(() => {
  //     this.animationTrigger.set(0);
  //   }, 4120);
  // }
}
